import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { JSX } from 'react';
import ExperienceLogo from '../Assets/Svg/ExperienceLogo';
import type { SelectChangeEvent, Theme} from '@mui/material';
import { Button, FormControl, Input, InputAdornment, InputLabel, MenuItem, Select, Switch, Typography, useTheme } from '@mui/material';
import type { SettingsContextProps } from '../Contexts/SettingsContext';
import SettingsContext from '../Contexts/SettingsContext';
import AppTheme from '../Enums/AppTheme';
import { faPlug, faUser } from '@fortawesome/free-solid-svg-icons';
import ApiService from '../Services/ApiService';
import type { LocalStorageContextProps } from '../Contexts/LocalStorageContext';
import LocalStorageContext from '../Contexts/LocalStorageContext';
import type { ModeContextProps } from '../Contexts/ModeContext';
import ModeContext from '../Contexts/ModeContext';
import Mode from '../Enums/Mode';
import type { ApiContextProps } from '../Contexts/ApiContext';
import ApiContext from '../Contexts/ApiContext';
import { appVersion } from '../Constants';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type KFleetToken from '../Models/KFleetTokenModel';
import { updateLastActivity } from '../Utils/LastActivity';
import CurrentUser from '../Models/CurrentUserModel';
import type { UserContextProps } from '../Contexts/UserContext';
import UserContext from '../Contexts/UserContext';
import type { TokenContextProps } from '../Contexts/TokenContext';
import TokenContext from '../Contexts/TokenContext';
import TokenType from '../Enums/TokenType';
import type Tenant from '../Models/TenantModel';
import type TenantToken from '../Models/TenantTokenModel';
import type { SystemContextProps } from '../Contexts/SystemContext';
import SystemContext from '../Contexts/SystemContext';
import type { IndexedDBContextProps } from '../Contexts/IndexedDBContext';
import IndexedDBContext from '../Contexts/IndexedDBContext';
import LocalStorageService from '../Services/LocalStorageService';
import { useTitle, shakeForm, formFocusNextKeyboard } from 'shouldve-been-react';
import { InstallPWAComponent } from 'react-pwa-tools';
import { ErrorMessage, LoadingButton, NumberInput, PasswordInput, SuccessMessage, UrlInput } from 'shouldve-been-mui';

const apiPath: string = '/api';

function ConnectPage(): JSX.Element {

    useTitle('K-Fleet Experience - Connect');

    const settingsContext: SettingsContextProps = useContext<SettingsContextProps>(SettingsContext);
    const localStorageContext: LocalStorageContextProps = useContext<LocalStorageContextProps>(LocalStorageContext);
    const indexedDBContext: IndexedDBContextProps = useContext<IndexedDBContextProps>(IndexedDBContext);
    const modeContext: ModeContextProps = useContext<ModeContextProps>(ModeContext);
    const apiContext: ApiContextProps = useContext<ApiContextProps>(ApiContext);
    const userContext: UserContextProps = useContext<UserContextProps>(UserContext);
    const tokenContext: TokenContextProps = useContext<TokenContextProps>(TokenContext);
    const systemContext: SystemContextProps = useContext<SystemContextProps>(SystemContext);

    const currentTheme: Theme = useTheme();

    const formRef: React.RefObject<HTMLFormElement> = useRef<HTMLFormElement>(null);

    const [connectMode, setConnectMode] = useState<'tenantStep1' | 'tenantStep2' | 'directStep1' | 'directStep2'>('tenantStep1');

    //for tenantStep1
    const [email, setEmail] = useState<string>('');
    const [tenantPassword, setTenantPassword] = useState<string>('');
    const [tenantStep1ConnectPending, setTenantStep1ConnectPending] = useState<boolean>(false);

    //for tenantStep2
    const [availableTenants, setAvailableTenants] = useState<Tenant[]>([]);
    const [tenantDisplayName, setTenantDisplayName] = useState<string>('');
    const [tenantStep2ConnectPending, setTenantStep2ConnectPending] = useState<boolean>(false);

    //for directStep1
    const [protocol, setProtocol] = useState<string>('http://');
    const [url, setUrl] = useState<string>('');
    const [port, setPort] = useState<string>('');
    const [directConnectPending, setDirectConnectPending] = useState<boolean>(false);

    //for directStep2
    const [username, setUsername] = useState<string>('');
    const [directPassword, setDirectPassword] = useState<string>('');
    const [directLoginPending, setDirectLoginPending] = useState<boolean>(false);

    const [api, setApi] = useState<ApiService | null>(null);
    
    const [deleteAllDataPending, setDeleteAllDataPending] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [windowSize, setWindowSize] = useState<[number, number]>([1920, 1080]);

    const tenantMenuItems: JSX.Element[] = useMemo<JSX.Element[]>(() => {

        return availableTenants.map((tenant: Tenant) => {
            return (
                <MenuItem 
                    key={tenant.tenantId}
                    value={tenant.displayName}
                >
                    {tenant.displayName}
                </MenuItem> 
            );
        });
    }, [availableTenants]);

    useEffect(() => {

        function updateSize(): void {
            setWindowSize([window.innerWidth, window.innerHeight]);
        }

        window.addEventListener('resize', updateSize);
        updateSize();
        return () => window.removeEventListener('resize', updateSize);
    }, []);

    function updateTheme(theme: AppTheme): void {

        settingsContext.setTheme(theme);
        localStorageContext.localStorageService.setTheme(theme);
    }

    function handleKeyboardSubmitTenantStep1(e: React.KeyboardEvent): void {

        if (e.key === 'Enter') {
            e.preventDefault();
            void handleSubmitTenantStep1();
        }
    }

    function handleKeyboardSubmitDirectStep1(e: React.KeyboardEvent): void {

        if (e.key === 'Enter') {
            e.preventDefault();
            void handleSubmitDirectStep1();
        }
    }

    function handleKeyboardSubmitDirectStep2(e: React.KeyboardEvent): void {

        if (e.key === 'Enter') {
            e.preventDefault();
            void handleSubmitDirectStep2();
        }
    }

    async function handleSubmitTenantStep1(): Promise<void> {

        setTenantStep1ConnectPending(true);

        const tenants: Tenant[] = await ApiService.getTenantsByEmail(email);

        if (tenants.length === 0) {
            setErrorMessage('Email is not connected to any tenants');
            setSuccessMessage(null);
            shakeForm(formRef);
        } else if (tenants.length === 1) {
            await handleTenantConnect(tenants[0]?.displayName ?? '', tenants[0]?.tunnelUrl ?? '');
        } else {
            setAvailableTenants(tenants);
            setConnectMode('tenantStep2');
            setErrorMessage(null);
            setSuccessMessage(null);
        }

        setTenantStep1ConnectPending(false);
    }

    async function handleSubmitTenantStep2(): Promise<void> {

        setTenantStep2ConnectPending(true);

        const tunnelUrl: string | null = availableTenants.find((tenant: Tenant) => tenant.displayName === tenantDisplayName)?.tunnelUrl ?? null;

        if (tunnelUrl === null) {
            setErrorMessage('Could not find tunnelUrl');
            setSuccessMessage(null);
        } else {
            await handleTenantConnect(tenantDisplayName, tunnelUrl);
        }
    

        setTenantStep2ConnectPending(false);
    }

    async function handleTenantConnect(currentTenantDisplayName: string, tunnelUrl: string): Promise<void> {

        const tokenInfo: TenantToken | null = await ApiService.fetchNewTenantTokenByEmailAndPassword(email, tenantPassword, currentTenantDisplayName) ?? null;
    
        const token: string | null = tokenInfo?.token ?? null;
        const refreshToken: string | null = tokenInfo?.refreshToken ?? null;
        const expiration: Date | null = tokenInfo?.expiration ?? null;
    
        if (token === null || refreshToken === null || expiration === null) {
    
            setErrorMessage('Could not login');    
            setSuccessMessage(null);   
            shakeForm(formRef);
            return;
        }

        const newApi: ApiService = new ApiService('https://' + tunnelUrl + apiPath);
    
        const user: CurrentUser | null = await newApi?.getCurrentUserByEmail(email, token) ?? null;

        if (user === null) {

            setErrorMessage('Could not login');
            setSuccessMessage(null);
            shakeForm(formRef);
            return;
        }
    
        userContext.setCurrentUser(user);
        localStorageContext.localStorageService.setCurrentUser(user);
        updateLastActivity(userContext, localStorageContext);
        
        modeContext.setMode(Mode.CREATE_PIN);
        localStorageContext.localStorageService.setMode(Mode.CREATE_PIN);

        apiContext.setApi(newApi);
        localStorageContext.localStorageService.setApi(newApi);

        systemContext.setTenantDisplayName(currentTenantDisplayName);
        localStorageContext.localStorageService.setTenantDisplayName(currentTenantDisplayName);
    
        tokenContext.setToken(token);
        tokenContext.setRefreshToken(refreshToken);
        tokenContext.setExpiration(expiration);
        tokenContext.setTokenType(TokenType.TENANT);
        localStorageContext.localStorageService.setToken(token);
        localStorageContext.localStorageService.setRefreshToken(refreshToken);
        localStorageContext.localStorageService.setExpiration(expiration);
        localStorageContext.localStorageService.setTokenType(TokenType.TENANT);
    }

    async function handleSubmitDirectStep1(): Promise<void> {

        setDirectConnectPending(true);

        if (url === '' || port === '') {
            setErrorMessage('Connect failed');
            setSuccessMessage(null);
            shakeForm(formRef);
        } else {
            const fullUrl: string = protocol + url + ':' + port + apiPath;
            const apiService: ApiService = new ApiService(fullUrl);
            const hasConnection: boolean = await apiService.getHealth(null);
            
            if (!hasConnection) {
                setErrorMessage('Connect failed');
                setSuccessMessage(null);
                shakeForm(formRef);
            } else {
                setApi(apiService);
                setConnectMode('directStep2');
                setErrorMessage(null);
                setSuccessMessage(null);
            }
        }

        setDirectConnectPending(false);
    }

    async function handleSubmitDirectStep2(): Promise<void> {

        setDirectLoginPending(true);
    
        const tokenInfo: KFleetToken | null = await api?.fetchNewKFleetTokenByUsernameAndPassword(username, directPassword) ?? null;
    
        const token: string | null = tokenInfo?.token ?? null;
        const refreshToken: string | null = tokenInfo?.refreshToken ?? null;
        const userId: string | null = tokenInfo?.userId ?? null;
        const newEmail: string | null = tokenInfo?.email ?? null;
        const access: string[] | null = tokenInfo?.access ?? null;
        const expiration: Date | null = tokenInfo?.expiration ?? null;
    
        if (token === null || refreshToken === null || userId === null || newEmail === null || access === null || expiration === null) {
    
            setDirectLoginPending(false);
            setErrorMessage('Could not login');
            setSuccessMessage(null);
               
            shakeForm(formRef);
    
            return;
        }
    
        const user: CurrentUser = new CurrentUser(userId, username, newEmail, access);
    
        userContext.setCurrentUser(user);
        localStorageContext.localStorageService.setCurrentUser(user);
        updateLastActivity(userContext, localStorageContext);
        
        modeContext.setMode(Mode.CREATE_PIN);
        localStorageContext.localStorageService.setMode(Mode.CREATE_PIN);

        apiContext.setApi(api);
        localStorageContext.localStorageService.setApi(api);
    
        tokenContext.setToken(token);
        tokenContext.setRefreshToken(refreshToken);
        tokenContext.setExpiration(expiration);
        tokenContext.setTokenType(TokenType.KFLEET);
        localStorageContext.localStorageService.setToken(token);
        localStorageContext.localStorageService.setRefreshToken(refreshToken);
        localStorageContext.localStorageService.setExpiration(expiration);
        localStorageContext.localStorageService.setTokenType(TokenType.KFLEET);
    
        setDirectLoginPending(false);
    }

    function handleTenantBack(): void {

        setAvailableTenants([]);
        setConnectMode('tenantStep1');
        setErrorMessage(null);
        setSuccessMessage(null);
    }

    function handleDirectBack(): void {

        setApi(null);
        setConnectMode('directStep1');
        setErrorMessage(null);
        setSuccessMessage(null);
    }

    async function handleDeleteAllData(): Promise<void> {

        setDeleteAllDataPending(true);

        LocalStorageService.clearAllData();
        const success: boolean = await indexedDBContext.indexedDBService.deleteDatabase();

        if (!success) {
            setErrorMessage('Failed to delete all data');
        } else {
            setSuccessMessage('All data deleted');
        }

        setDeleteAllDataPending(false);
    }

    return (
        <div style={{
            padding: '5px',
            height: windowSize[1],
            overflow: 'auto',
        }}>
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '10px 10px 0px 10px',
            }}>
                <div style={{
                    textAlign: 'center',
                }}>
                    <Typography
                        style={{
                            color: currentTheme.palette.text.primary,
                        }}
                    >
                        Version
                    </Typography>
                    <Typography
                        style={{
                            color: currentTheme.palette.text.primary,
                        }}
                    >
                        {appVersion}
                    </Typography>
                </div>
                <div style={{
                    textAlign: 'right',
                }}>
                    <a
                        href='/LICENSE.md'
                        target='_blank'
                        rel='noreferrer'
                        style={{
                            color: currentTheme.palette.text.primary,
                        }}
                    >
                        Licenses
                    </a>
                    <table style={{
                        marginLeft: 'auto',
                    }}>
                        <tbody>
                            <tr>
                                <td style={{
                                    color: currentTheme.palette.text.primary,
                                }}>
                                    Dark Mode
                                </td>
                                <td>
                                    <FormControl>
                                        <Switch
                                            checked={settingsContext.theme === AppTheme.DARK_MODE}
                                            onChange={(e: React.ChangeEvent): void => updateTheme((e.target as HTMLInputElement).checked ? AppTheme.DARK_MODE : AppTheme.LIGHT_MODE)}
                                            color='primary'
                                        />           
                                    </FormControl>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
            <div 
                style={{
                    textAlign: 'center',
                    marginTop: '30px',
                }}
            >
                <ExperienceLogo
                    color={currentTheme.palette.text.primary}
                    height={100}
                />
                <Typography
                    variant='h5'
                    style={{
                        color: currentTheme.palette.text.primary,
                        fontFamily: 'Permanent Marker',
                    }}    
                >
                    K-Fleet Experience
                </Typography>
                <form noValidate autoComplete='off' ref={formRef} name='connect'>
                    {connectMode === 'tenantStep1' && 
                        <>
                            <table style={{
                                marginLeft: 'auto',
                                marginRight: 'auto',
                                marginTop: '40px',
                            }}>
                                <tbody>
                                    <tr>
                                        <td>
                                            <span style={{
                                                color: currentTheme.palette.text.primary,
                                            }}>
                                                Remote
                                            </span>
                                        </td>
                                        <td>
                                            <Switch
                                                checked={false}
                                                onChange={(e: React.ChangeEvent): void => {
                                                    setConnectMode((e.target as HTMLInputElement).checked ? 'directStep1' : 'tenantStep1');
                                                    setErrorMessage(null);
                                                    setSuccessMessage(null);
                                                }}
                                                color='primary'
                                            />  
                                        </td>
                                        <td>
                                            <span style={{
                                                color: currentTheme.palette.text.primary,
                                            }}>
                                                Local
                                            </span>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                            <br />
                            <FormControl style={{
                                minWidth: Math.min(windowSize[0] * 0.8, 300),
                                maxWidth: Math.min(windowSize[0] * 0.8, 300),
                            }}>
                                <InputLabel>Email</InputLabel>
                                <Input 
                                    value={email}
                                    onChange={(e: React.ChangeEvent): void => setEmail((e.target as HTMLInputElement).value)}
                                    onKeyDown={formFocusNextKeyboard}
                                    margin='dense'
                                    startAdornment={
                                        <InputAdornment position='start'>
                                            <FontAwesomeIcon icon={faUser} />
                                        </InputAdornment>
                                    }
                                />
                            </FormControl>
                            <br />
                            <br />
                            <PasswordInput 
                                password={tenantPassword} 
                                onPasswordChange={setTenantPassword}
                                onKeyDown={handleKeyboardSubmitTenantStep1}
                                minWidth={Math.min(windowSize[0] * 0.8, 300)}
                                maxWidth={Math.min(windowSize[0] * 0.8, 300)}
                            />
                            <br />
                            <br />
                            <LoadingButton 
                                width='150px'
                                height='40px'
                                text='Login'
                                controller={tenantStep1ConnectPending}
                                onClick={handleSubmitTenantStep1}
                                buttonColor='primary'
                                circularColor='error'
                                circularSize='30px'
                                variant='contained'
                            />
                            <br />
                            <br />
                            <LoadingButton 
                                width='120px'
                                height='32px'
                                text='Delete All Data'
                                controller={deleteAllDataPending}
                                onClick={handleDeleteAllData}
                                buttonColor='error'
                                circularColor='primary'
                                circularSize='30px'
                                variant='contained'
                                fontSize='10px'
                            />
                        </>
                    }
                    {connectMode === 'tenantStep2' && 
                        <>
                            <FormControl 
                                size='small'
                                style={{
                                    width: Math.min(windowSize[0] * 0.8, 500),
                                    marginTop: '40px',
                                    textAlign: 'left',
                                }}
                            >
                                <InputLabel>Select Remote Tunnel</InputLabel>
                                <Select
                                    value={tenantDisplayName}
                                    onChange={(e: SelectChangeEvent): void => setTenantDisplayName((e.target as HTMLInputElement).value)}
                                    size='small'
                                    variant='outlined'
                                    label='Select Remote Tunnel'
                                >
                                    {tenantMenuItems}
                                </Select>
                            </FormControl>
                            <br />
                            <br />
                            <LoadingButton 
                                width='150px'
                                height='40px'
                                text='Login'
                                controller={tenantStep2ConnectPending}
                                onClick={handleSubmitTenantStep2}
                                buttonColor='primary'
                                circularColor='error'
                                circularSize='30px'
                                variant='contained'
                            />
                            <br />
                            <br />
                            <Button 
                                onClick={handleTenantBack}
                                color='error'
                                variant='contained'
                                style={{
                                    minWidth: '120px',
                                    maxWidth: '120px',
                                    minHeight: '32px',
                                    maxHeight: '32px',
                                }}
                            >
                                Back
                            </Button>
                        </>
                    }
                    {connectMode === 'directStep1' && 
                        <>
                            <table style={{
                                marginLeft: 'auto',
                                marginRight: 'auto',
                                marginTop: '40px',
                            }}>
                                <tbody>
                                    <tr>
                                        <td>
                                            <span style={{
                                                color: currentTheme.palette.text.primary,
                                            }}>
                                                Remote
                                            </span>
                                        </td>
                                        <td>
                                            <Switch
                                                checked={true}
                                                onChange={(e: React.ChangeEvent): void => {
                                                    setConnectMode((e.target as HTMLInputElement).checked ? 'directStep1' : 'tenantStep1');
                                                    setErrorMessage(null);
                                                    setSuccessMessage(null);
                                                }}
                                                color='primary'
                                            />  
                                        </td>
                                        <td>
                                            <span style={{
                                                color: currentTheme.palette.text.primary,
                                            }}>
                                                Local
                                            </span>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                            <br />
                            <UrlInput 
                                protocol={protocol}
                                url={url}
                                onProtocolChange={setProtocol}
                                onUrlChange={setUrl}
                                onKeyDown={formFocusNextKeyboard}
                                minWidth={Math.min(windowSize[0] * 0.8, 300)}
                                maxWidth={Math.min(windowSize[0] * 0.8, 300)}
                            />
                            <br />
                            <br />
                            <NumberInput 
                                labelText='Port'
                                value={port}
                                onChange={setPort}
                                onKeyDown={handleKeyboardSubmitDirectStep1}
                                icon={faPlug}
                                minValue={0}
                                maxValue={65535}
                                minWidth={Math.min(windowSize[0] * 0.8, 300)}
                                maxWidth={Math.min(windowSize[0] * 0.8, 300)}
                                fontSize='16px'
                            />
                            <br />
                            <br />
                            <LoadingButton
                                width='150px'
                                height='40px'
                                text='Connect'
                                controller={directConnectPending}
                                onClick={handleSubmitDirectStep1}
                                circularColor='error'
                                buttonColor='primary'
                                circularSize='30px'
                                variant='contained'
                            />
                            <br />
                            <br />
                            <LoadingButton 
                                width='120px'
                                height='32px'
                                text='Delete All Data'
                                controller={deleteAllDataPending}
                                onClick={handleDeleteAllData}
                                buttonColor='error'
                                circularColor='primary'
                                circularSize='30px'
                                variant='contained'
                                fontSize='10px'
                            />
                        </>
                    }
                    {connectMode === 'directStep2' && 
                        <>
                            <FormControl style={{
                                minWidth: Math.min(windowSize[0] * 0.8, 300),
                                maxWidth: Math.min(windowSize[0] * 0.8, 300),
                                marginTop: '40px',
                            }}>
                                <InputLabel>Username</InputLabel>
                                <Input 
                                    value={username}
                                    onChange={(e: React.ChangeEvent): void => setUsername((e.target as HTMLInputElement).value)}
                                    onKeyDown={formFocusNextKeyboard}
                                    margin='dense'
                                    startAdornment={
                                        <InputAdornment position='start'>
                                            <FontAwesomeIcon icon={faUser} />
                                        </InputAdornment>
                                    }
                                />
                            </FormControl>
                            <br />
                            <br />
                            <PasswordInput 
                                password={directPassword} 
                                onPasswordChange={setDirectPassword}
                                onKeyDown={handleKeyboardSubmitDirectStep2}
                                minWidth={Math.min(windowSize[0] * 0.8, 300)}
                                maxWidth={Math.min(windowSize[0] * 0.8, 300)}
                            />
                            <br />
                            <br />
                            <LoadingButton 
                                width='150px'
                                height='40px'
                                text='Login'
                                controller={directLoginPending}
                                onClick={handleSubmitDirectStep2}
                                buttonColor='primary'
                                circularColor='error'
                                circularSize='30px'
                                variant='contained'
                            />
                            <br />
                            <br />
                            <Button 
                                onClick={handleDirectBack}
                                color='error'
                                variant='contained'
                                style={{
                                    minWidth: '120px',
                                    maxWidth: '120px',
                                    minHeight: '32px',
                                    maxHeight: '32px',
                                }}
                            >
                                Back
                            </Button>
                        </>
                    }
                </form>
                <br />
                <div style={{
                    textAlign: 'center',
                }}>
                    <ErrorMessage
                        message={errorMessage}
                        maxWidth={Math.min(windowSize[0] * 0.8, 300)}
                    />
                    <SuccessMessage
                        message={successMessage}
                        maxWidth={Math.min(windowSize[0] * 0.8, 300)}
                    />
                </div>
                <br />
                <br />
                <InstallPWAComponent 
                    applicationName='K-Fleet Experience'
                />
            </div>
        </div>
    );
}

export default ConnectPage;